import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

import VerificationSuccess from './components/VerificationSuccess';
import VerificationError from './components/VerificationError';
import VerificationLoading from './components/VerificationLoading';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    verifyEmail();
  }, []);

  const verifyEmail = async () => {
    try {
      // Get the session to check if verification was successful
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

      if (sessionError) {
        throw sessionError;
      }

      if (session?.user) {
        // User is authenticated - email verification successful
        setEmail(session?.user?.email);
        setStatus('success');

        // Check if user has confirmed their email
        const { data: { user }, error: userError } = await supabase?.auth?.getUser();
        
        if (userError) {
          throw userError;
        }

        if (user?.email_confirmed_at) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage('Email verification is still pending. Please check your inbox.');
        }
      } else {
        // Check if there's a verification token in the URL
        const token = searchParams?.get('token');
        const type = searchParams?.get('type');

        if (!token || type !== 'email') {
          setStatus('error');
          setErrorMessage('Invalid verification link. Please check your email for the correct link.');
          return;
        }

        // Token exists but no session - might be expired
        setStatus('expired');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');
      setErrorMessage(error?.message || 'Failed to verify email. Please try again.');
    }
  };

  const handleContinueToLogin = () => {
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleResendVerification = async () => {
    try {
      setStatus('loading');
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: email
      });

      if (error) throw error;

      setStatus('success');
      setErrorMessage('Verification email resent! Please check your inbox.');
    } catch (error) {
      console.error('Resend verification error:', error);
      setStatus('error');
      setErrorMessage(error?.message || 'Failed to resend verification email.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        {status === 'loading' && <VerificationLoading />}
        {status === 'success' && (
          <VerificationSuccess
            email={email}
            onContinueToLogin={handleContinueToLogin}
            onGoToDashboard={handleGoToDashboard}
          />
        )}
        {status === 'error' && (
          <VerificationError
            errorMessage={errorMessage}
            onContinueToLogin={handleContinueToLogin}
            onResendVerification={email ? handleResendVerification : null}
          />
        )}
        {status === 'expired' && (
          <VerificationError
            errorMessage="This verification link has expired. Please request a new verification email."
            onContinueToLogin={handleContinueToLogin}
            onResendVerification={email ? handleResendVerification : null}
          />
        )}
      </div>
    </div>
  );
};

export default EmailVerification;